import React, { useState } from 'react';
import Modal from '../Modal';
import { Conditionnement } from '../../types/weighing';

// Mapping des valeurs d'affichage vers les valeurs de base de données
const TYPE_MAPPING = {
  'Sac': 'sac',
  'Carton': 'carton', 
  'Bidon': 'bidon',
  'Palette': 'palette',
  'Bigbag': 'autre',
  'Autre': 'autre'
} as const;

const UNITE_MAPPING = {
  'Kilogrammes (kg)': 'kg',
  'Grammes (g)': 'g',
  'Litres (l)': 'l',
  'Millilitres (ml)': 'ml',
  'Pieces': 'pieces'
} as const;

const STATUT_MAPPING = {
  'Actif': 'actif',
  'Inactif': 'inactif'
} as const;

interface AddPackagingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (packaging: Partial<Conditionnement>) => void;
}

const AddPackagingModal: React.FC<AddPackagingModalProps> = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState<Partial<Conditionnement>>({
    nom: '',
    type: 'Sac',
    capacite: 0,
    unite: 'Kilogrammes (kg)',
    dimensions: '',
    poids_tare: 0,
    statut: 'Actif'
  });

  const [errors, setErrors] = useState<Partial<Record<keyof Conditionnement, string>>>({});

  const handleInputChange = (field: keyof Conditionnement, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof Conditionnement, string>> = {};

    if (!formData.nom?.trim()) {
      newErrors.nom = 'Le nom est requis';
    }
    if (!formData.capacite || formData.capacite <= 0) {
      newErrors.capacite = 'La capacité doit être supérieure à 0';
    }
    if (formData.poids_tare !== undefined && formData.poids_tare < 0) {
      newErrors.poids_tare = 'Le poids de tare ne peut pas être négatif';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Convertir les valeurs d'affichage vers les valeurs de base de données
      const dataToSave = {
        ...formData,
        type: TYPE_MAPPING[formData.type as keyof typeof TYPE_MAPPING] || formData.type?.toLowerCase(),
        unite: UNITE_MAPPING[formData.unite as keyof typeof UNITE_MAPPING] || formData.unite?.toLowerCase(),
        statut: STATUT_MAPPING[formData.statut as keyof typeof STATUT_MAPPING] || formData.statut?.toLowerCase()
      };
      
      onSave(dataToSave);
      onClose();
      setFormData({
        nom: '',
        type: 'Sac',
        capacite: 0,
        unite: 'Kilogrammes (kg)',
        dimensions: '',
        poids_tare: 0,
        statut: 'Actif'
      });
      setErrors({});
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Ajouter un nouveau conditionnement" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Nom et Type */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom du conditionnement <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nom}
              onChange={(e) => handleInputChange('nom', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.nom ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Ex: Sac de 50kg"
            />
            {errors.nom && (
              <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type d'emballage 
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="Sac">Sac</option>
              <option value="Carton">Carton</option>
              <option value="Bidon">Bidon</option>
              <option value="Palette">Palette</option>
              <option value="Bigbag">Bigbag</option>
              <option value="Autre">Autre</option>
            </select>
          </div>
        </div>

        {/* Capacité et Unité */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Capacité 
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.capacite}
              onChange={(e) => handleInputChange('capacite', parseFloat(e.target.value) || 0)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.capacite ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="0.00"
            />
            {errors.capacite && (
              <p className="mt-1 text-sm text-red-600">{errors.capacite}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Unité
            </label>
            <select
              value={formData.unite}
              onChange={(e) => handleInputChange('unite', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="Kilogrammes (kg)">Kilogrammes (kg)</option>
              <option value="Grammes (g)">Grammes (g)</option>
              <option value="Litres (l)">Litres (l)</option>
              <option value="Millilitres (ml)">Millilitres (ml)</option>
              <option value="Pieces">Pièces</option>
            </select>
          </div>
        </div>

        {/* Dimensions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Dimensions 
          </label>
          <input
            type="text"
            value={formData.dimensions}
            onChange={(e) => handleInputChange('dimensions', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
              errors.dimensions ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Ex: 60x40x20 cm"
          />
          {errors.dimensions && (
            <p className="mt-1 text-sm text-red-600">{errors.dimensions}</p>
          )}
        </div>

        {/* Poids de tare et Statut */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Poids de tare (kg)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.poids_tare}
              onChange={(e) => handleInputChange('poids_tare', parseFloat(e.target.value) || 0)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.poids_tare ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="0.00"
            />
            {errors.poids_tare && (
              <p className="mt-1 text-sm text-red-600">{errors.poids_tare}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut
            </label>
            <select
              value={formData.statut}
              onChange={(e) => handleInputChange('statut', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="Actif">Actif</option>
              <option value="Inactif">Inactif</option>
            </select>
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Ajouter le conditionnement
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddPackagingModal;
